/* eslint-disable react-hooks/rules-of-hooks */
import { useMemo, useState } from 'react';

import useOverriddenState from '../useOverriddenState/index.ts';

const noop = () => 0;
const DEFAULT_EMPTY_OPTIONS = {};
export interface UseBooleanOptions {
  /**
   * Fired whenever the current state is set
   */
  onChange?: (currentValue: boolean) => void;
  /**
   * If set to a boolean will override the current state with the boolean's state
   */
  overrideWith?: null | boolean;
}

export interface UseBooleanState {
  /**
   * The current state
   */
  value: boolean;
  /**
   * Allows changing the current state to `value`
   */
  set: (value: boolean) => void;
  /**
   * Toggles the current state
   */
  toggle: () => void;
  /**
   * Sets the current state to `true`
   */
  setTrue: () => void;
  /**
   * Sets the current state to `false`
   */
  setFalse: () => void;
}
/**
 * Helper Hook for holding state of a boolean state
 * @param {boolean} initialState
 * @param {Object} [options]
 * @param {Function} [options.onChange] Fires on change of the state
 * @param {boolean} [options.overrideWith] If specified, overrides internal state (still onChanges)
 *
 * @example <caption>Example Usage</caption>
 * const {value, setTrue, setFalse, toggle, set} = useBoolean(initialValue, {
 *   onChange: (v) => console.log(v),
 *   overrideWith: value
 * })
 */
export default function useBoolean(
  initialState: boolean,
  options: UseBooleanOptions = DEFAULT_EMPTY_OPTIONS
): UseBooleanState {
  const { onChange = noop, overrideWith = null } = options;
  // Not using any of the advanced options, just back it by a single hook for performance
  const [value, setValue] =
    options === DEFAULT_EMPTY_OPTIONS
      ? useState(Boolean(initialState))
      : useOverriddenState(Boolean(initialState), overrideWith, { onChange });

  const setters = useMemo(
    () => ({
      set: (v: unknown) => setValue(Boolean(v)),
      toggle: () => setValue(v => !v),
      setTrue: () => setValue(true),
      setFalse: () => setValue(false)
    }),
    [setValue]
  );

  return {
    value,
    set: setters.set,
    toggle: setters.toggle,
    setTrue: setters.setTrue,
    setFalse: setters.setFalse
  };
}
